import { MAJOR_PATHS } from '../majorPaths';

export const MAJOR_REPORTS_MEMBER_TYPES = [
  { value: '', labelKey: 'FILTER_MEMBER_TYPE_ALL' },
  { value: 'A', labelKey: 'FILTER_MEMBER_TYPE_A' },
  { value: 'C', labelKey: 'FILTER_MEMBER_TYPE_C' },
  { value: 'E', labelKey: 'FILTER_MEMBER_TYPE_E' },
  { value: 'Externe', labelKey: 'FILTER_MEMBER_TYPE_EXT' }
];

export const MAJOR_REPORTS_SEXES = [
  { value: '', labelKey: 'FILTER_SEX_ALL' },
  { value: 'M', labelKey: 'FILTER_SEX_M' },
  { value: 'F', labelKey: 'FILTER_SEX_F' }
];

export const MAJOR_REPORTS_AGE_PRESETS = [
  { key: 'child', min: 0, max: 17, labelKey: 'FILTER_AGE_CHILD' },
  { key: 'adult', min: 18, max: 59, labelKey: 'FILTER_AGE_ADULT' },
  { key: 'senior', min: 60, max: null, labelKey: 'FILTER_AGE_SENIOR' }
];

/** Filtres avancés partagés (hors période / site). */
export const DEFAULT_MAJOR_REPORTS_ADVANCED_FILTERS = {
  q: '',
  act_q: '',
  company_id: '',
  member_type: '',
  sexe: '',
  age_min: '',
  age_max: ''
};

const TAB_ACT_KIND = {
  [MAJOR_PATHS.RAPPORTS_SOINS]: 'care',
  [MAJOR_PATHS.RAPPORTS_INJECTIONS]: 'injection',
  [MAJOR_PATHS.RAPPORTS_TESTS]: 'test',
  [MAJOR_PATHS.RAPPORTS_VACCINS]: 'vaccine',
  [MAJOR_PATHS.RAPPORTS_PLANNING_FAMILIAL]: 'family_planning'
};

const ACT_KIND_LABEL_KEYS = {
  care: 'FILTER_ACT_KIND_CARE',
  injection: 'FILTER_ACT_KIND_INJECTION',
  test: 'FILTER_ACT_KIND_TEST',
  vaccine: 'FILTER_ACT_KIND_VACCINE',
  family_planning: 'FILTER_ACT_KIND_FAMILY_PLANNING'
};

/** Nature d'acte implicite selon l'onglet rapports (null = synthèse, tous les actes). */
export function resolveMajorReportsActKind(pathname = '') {
  const path = String(pathname || '').replace(/\/+$/, '');
  if (path === MAJOR_PATHS.RAPPORTS || path.endsWith('/rapports') || path.endsWith('/infirmerie')) {
    return null;
  }
  if (TAB_ACT_KIND[path]) return TAB_ACT_KIND[path];
  // Admin monte les mêmes pages sous /admin/reports/analytics/infirmerie/...
  if (path.endsWith('/soins')) return 'care';
  if (path.endsWith('/injections')) return 'injection';
  if (path.endsWith('/tests')) return 'test';
  if (path.endsWith('/vaccins')) return 'vaccine';
  if (path.endsWith('/planning-familial')) return 'family_planning';
  return null;
}

export function isMajorReportsTabScoped(pathname = '') {
  return resolveMajorReportsActKind(pathname) != null;
}

export function detectActiveAgePreset(advanced = {}) {
  const min = advanced.age_min === '' ? null : Number(advanced.age_min);
  const max = advanced.age_max === '' ? null : Number(advanced.age_max);
  return (
    MAJOR_REPORTS_AGE_PRESETS.find(
      (preset) => preset.min === min && (preset.max ?? null) === (max ?? null)
    )?.key || null
  );
}

export function hasAgeFilter(advanced = {}) {
  return (
    (advanced.age_min !== '' && advanced.age_min != null) ||
    (advanced.age_max !== '' && advanced.age_max != null)
  );
}

export function countAgeFilters(advanced = {}) {
  return hasAgeFilter(advanced) ? 1 : 0;
}

export function countActiveMajorReportFilters(advanced = {}, { includeActKind = false, actKind = null } = {}) {
  let count = 0;
  if (String(advanced.q || '').trim()) count += 1;
  if (String(advanced.act_q || '').trim()) count += 1;
  if (advanced.company_id) count += 1;
  if (advanced.member_type) count += 1;
  if (advanced.sexe) count += 1;
  if (hasAgeFilter(advanced)) count += 1;
  if (includeActKind && actKind) count += 1;
  return count;
}

export function isDefaultMajorReportsAdvancedFilters(advanced = {}) {
  return countActiveMajorReportFilters(advanced) === 0;
}

export function buildMajorReportsApiParams({
  from,
  to,
  site_id,
  limit,
  pathname,
  q,
  act_q,
  company_id,
  member_type,
  sexe,
  age_min,
  age_max,
  act_kind
}) {
  const params = { from, to, limit };
  if (site_id) params.site_id = site_id;
  const trimmedQ = String(q || '').trim();
  const trimmedActQ = String(act_q || '').trim();
  if (trimmedQ) params.q = trimmedQ;
  if (trimmedActQ) params.act_q = trimmedActQ;
  if (company_id) params.company_id = parseInt(company_id, 10);
  if (member_type) params.member_type = member_type;
  if (sexe) params.sexe = sexe;
  if (age_min !== '' && age_min != null) params.age_min = parseInt(age_min, 10);
  if (age_max !== '' && age_max != null) params.age_max = parseInt(age_max, 10);

  const tabActKind = act_kind !== undefined ? act_kind : resolveMajorReportsActKind(pathname);
  if (tabActKind) params.act_kind = tabActKind;

  return params;
}

export function buildMajorReportsFilterChips(advanced, companies, copy, { actKind = null } = {}) {
  const chips = [];
  const company = companies.find((c) => String(c.id) === String(advanced.company_id));

  if (String(advanced.q || '').trim()) {
    chips.push({ key: 'q', label: `${copy.CHIP_PATIENT}: ${advanced.q.trim()}` });
  }
  if (String(advanced.act_q || '').trim()) {
    chips.push({ key: 'act_q', label: `${copy.CHIP_ACT}: ${advanced.act_q.trim()}` });
  }
  if (company) {
    chips.push({ key: 'company_id', label: `${copy.CHIP_COMPANY}: ${company.name}` });
  }
  if (advanced.member_type) {
    const option = MAJOR_REPORTS_MEMBER_TYPES.find((o) => o.value === advanced.member_type);
    const memberLabel = option ? copy[option.labelKey] : advanced.member_type;
    chips.push({ key: 'member_type', label: `${copy.CHIP_MEMBER_TYPE}: ${memberLabel}` });
  }
  if (advanced.sexe) {
    chips.push({
      key: 'sexe',
      label: `${copy.CHIP_SEX}: ${advanced.sexe === 'M' ? copy.FILTER_SEX_M : copy.FILTER_SEX_F}`
    });
  }
  if (actKind && ACT_KIND_LABEL_KEYS[actKind]) {
    chips.push({
      key: 'tab_scope',
      label: `${copy.CHIP_TAB_SCOPE}: ${copy[ACT_KIND_LABEL_KEYS[actKind]]}`,
      readonly: true
    });
  }
  if (hasAgeFilter(advanced)) {
    const minLabel = advanced.age_min !== '' ? advanced.age_min : '…';
    const maxLabel = advanced.age_max !== '' ? advanced.age_max : '…';
    chips.push({ key: 'age', label: `${copy.CHIP_AGE}: ${minLabel}–${maxLabel}` });
  }
  return chips;
}

export function getMemberTypeLabel(copy, value) {
  const option = MAJOR_REPORTS_MEMBER_TYPES.find((o) => o.value === value);
  return option ? copy[option.labelKey] : value;
}

export function getActKindLabel(copy, actKind) {
  const key = ACT_KIND_LABEL_KEYS[actKind];
  return key ? copy[key] : actKind;
}
